(() => {
  const config = window.PermaplantsMeasurement || {};
  let privacyReady = false;
  let googleLoaded = false;
  let metaLoaded = false;
  let clarityLoaded = false;
  let initialAnalyticsViewsTracked = false;
  let initialMetaPageTracked = false;

  const privacyAllows = (purpose) => {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return false;
    return purpose === 'marketing' ? privacy.marketingAllowed() : privacy.analyticsProcessingAllowed();
  };

  const loadScript = (src, id) => {
    if (id && document.getElementById(id)) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = src;
    if (id) script.id = id;
    document.head.appendChild(script);
  };

  const loadGoogle = () => {
    if (googleLoaded || !config.ga4Id || !privacyAllows('analytics')) return;
    googleLoaded = true;
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', config.ga4Id, { send_page_view: false });
    loadScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(config.ga4Id)}`, 'permaplants-ga4');
  };

  const loadMeta = () => {
    if (metaLoaded || !config.metaPixelId || !privacyAllows('marketing')) return;
    metaLoaded = true;
    if (!window.fbq) {
      const fbq = function fbq() { fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments); };
      fbq.queue = [];
      fbq.loaded = true;
      fbq.version = '2.0';
      window.fbq = fbq;
    }
    window.fbq('init', config.metaPixelId);
    loadScript('https://connect.facebook.net/en_US/fbevents.js', 'permaplants-meta-pixel');
  };

  const loadClarity = () => {
    if (clarityLoaded || !config.clarityId || !privacyAllows('analytics')) return;
    clarityLoaded = true;
    window.clarity = window.clarity || function clarity() {
      (window.clarity.q = window.clarity.q || []).push(arguments);
    };
    loadScript(`https://www.clarity.ms/tag/${encodeURIComponent(config.clarityId)}`, 'permaplants-clarity');
  };

  const shopifyPublish = (name, data, purpose) => {
    if (!privacyAllows(purpose) || !window.Shopify?.analytics?.publish) return;
    window.Shopify.analytics.publish(`permaplants:${name}`, data);
  };

  const track = (name, data = {}, purpose = 'analytics') => {
    if (!privacyReady || !privacyAllows(purpose)) return;
    shopifyPublish(name, data, purpose);
    window.dispatchEvent(new CustomEvent('permaplants:measurement', { detail: { name, data } }));

    if (googleLoaded && window.gtag) window.gtag('event', name, data);
    if (clarityLoaded && window.clarity) window.clarity('event', name);

    if (metaLoaded && window.fbq) {
      const metaEvents = {
        view_item: 'ViewContent',
        add_to_cart: 'AddToCart',
        begin_checkout: 'InitiateCheckout',
        generate_lead: 'Lead',
      };
      if (metaEvents[name]) window.fbq('track', metaEvents[name], data);
    }
  };

  const productPayload = () => {
    if (!config.product) return {};
    return {
      currency: config.product.currency,
      value: Number(config.product.priceCents || 0) / 100,
      content_ids: [String(config.product.variantId || config.product.id)],
      content_name: config.product.title,
      content_type: 'product',
      items: [{
        item_id: String(config.product.variantId || config.product.id),
        item_name: config.product.title,
        price: Number(config.product.priceCents || 0) / 100,
        quantity: 1,
      }],
    };
  };

  const trackInitialViews = () => {
    if (!privacyReady) return;
    if (!initialAnalyticsViewsTracked && privacyAllows('analytics')) {
      initialAnalyticsViewsTracked = true;
      track('page_view', config.page || {});
      if (config.product) track('view_item', productPayload());
    }
    if (!initialMetaPageTracked && metaLoaded && privacyAllows('marketing') && window.fbq) {
      initialMetaPageTracked = true;
      window.fbq('track', 'PageView');
    }
  };

  const loadProviders = () => {
    loadGoogle();
    loadMeta();
    loadClarity();
    trackInitialViews();
  };

  const initializePrivacy = () => {
    const ready = () => {
      privacyReady = true;
      loadProviders();
    };

    document.addEventListener('visitorConsentCollected', () => {
      privacyReady = true;
      loadProviders();
    });

    if (window.Shopify?.customerPrivacy) {
      ready();
      return;
    }

    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) ready();
      });
    }
  };

  const setupStickyAddToCart = () => {
    const sticky = document.querySelector('[data-product-sticky-atc]');
    const mainButton = document.querySelector('[id^="ProductSubmitButton-"]');
    if (!sticky || !mainButton) return;

    const stickyButton = sticky.querySelector('[data-sticky-atc-button]');
    const updateStickyState = () => {
      stickyButton.disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      sticky.querySelector('[data-sticky-atc-label]').textContent = mainButton.querySelector('span')?.textContent?.trim() || 'Add to cart';
    };

    const updateStickyVisibility = () => {
      const buttonRect = mainButton.getBoundingClientRect();
      const shouldShow = buttonRect.bottom < 0;
      sticky.classList.toggle('is-visible', shouldShow);
      sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };
    window.addEventListener('scroll', updateStickyVisibility, { passive: true });
    window.addEventListener('resize', updateStickyVisibility);
    updateStickyVisibility();

    stickyButton.addEventListener('click', () => {
      track('sticky_add_to_cart_click', productPayload());
      mainButton.click();
    });

    new MutationObserver(updateStickyState).observe(mainButton, {
      attributes: true,
      childList: true,
      subtree: true,
    });
    updateStickyState();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const sourceSticky = data.html.querySelector('[data-product-sticky-atc]');
        if (sourceSticky) {
          sticky.querySelector('[data-sticky-atc-price]').textContent = sourceSticky.querySelector('[data-sticky-atc-price]')?.textContent || '';
        }
        config.product.variantId = data.variant?.id || config.product.variantId;
        config.product.priceCents = data.variant?.price || config.product.priceCents;
        updateStickyState();
      });
    }
  };

  const setupCommerceTracking = () => {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, productVariantId, cartData }) => {
        const payload = cartData?.product_title ? {
          currency: config.product?.currency || '',
          value: Number(cartData.final_price || 0) / 100,
          content_ids: [String(productVariantId || cartData.id || '')],
          content_name: cartData.product_title,
          content_type: 'product',
          items: [{
            item_id: String(productVariantId || cartData.id || ''),
            item_name: cartData.product_title,
            price: Number(cartData.final_price || 0) / 100,
            quantity: Number(cartData.quantity || 1),
          }],
        } : productPayload();
        track('add_to_cart', { ...payload, source, variant_id: String(productVariantId || '') });
      });
    }

    document.addEventListener('permaplants:cart-opened', (event) => {
      const detail = event.detail || {};
      track('view_cart', {
        currency: detail.currency,
        value: Number(detail.totalCents || 0) / 100,
        cart_count: Number(detail.count || 0),
      });
    });

    document.addEventListener('click', (event) => {
      if (!event.target.closest('#CartDrawer-Checkout')) return;
      const cart = document.querySelector('[data-cart-state]');
      track('begin_checkout', {
        currency: cart?.dataset.cartCurrency,
        value: Number(cart?.dataset.cartTotalCents || 0) / 100,
        cart_count: Number(cart?.dataset.cartCount || 0),
      });
    });

    if (document.querySelector('[data-styling-guide-success]')) {
      track('generate_lead', { lead_source: 'styling_guide' }, 'marketing');
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    initializePrivacy();
    setupStickyAddToCart();
    setupCommerceTracking();
  });
})();
